/* eslint-disable react-hooks/exhaustive-deps */
import { useForm } from "react-hook-form";
import {
  ControlledDatePicker,
  ControlledInput,
  ControlledSelect,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { useUploadImage } from "../../store/useUploadImage";
import { IQualityInput, useQualityInput } from "../../store/useQualityInput";
import { FLOAT_REGEX } from "../../utils/regexValidations";

type Inputs = {
  dropDate: Date;
  totalWeight: number;
  pet?: number;
  pp?: number;
  hdpe?: number;
  ldpe?: number;
  ps?: number;
  drinkingCartoons?: number;
  misc?: number;
  nonPackagingPlastic?: number;
  nonPlasticOrganic?: number;
  qualityAssessment: 1 | 2 | 3 | 4 | 5;
  // feedbackOptions?: any;
};

function EditQualityInputScreen() {
  /** Hooks */
  const params = useParams();
  const {
    updateQualityInput,
    getQualityInput,
    oneQualityInput,
    updatedQualityInput,
    clearUpdatedQualityInput,
  } = useQualityInput();
  const { uploadImage, clearUploadedImage, uploadedImage, setUploadedImage } =
    useUploadImage();
  const navigate = useNavigate();
  const {
    watch,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [image, setImage] = useState<File | null>(null);

  /** Callbacks */
  const goBack = () => navigate(-1);
  const onSubmit = (data: Inputs) => {
    let body: IQualityInput = {
      dropDate: new Date(data.dropDate),
      totalWeight: parseFloat(data.totalWeight.toString()),
      qualityAssessment: data.qualityAssessment,
    };
    if (data.pet) body.pet = data.pet;
    if (data.pp) body.pp = data.pp;
    if (data.hdpe) body.hdpe = data.hdpe;
    if (data.ldpe) body.ldpe = data.ldpe;
    if (data.ps) body.ps = data.ps;
    if (data.drinkingCartoons) body.drinkingCartoons = data.drinkingCartoons;
    if (data.misc) body.misc = data.misc;
    if (data.nonPackagingPlastic)
      body.nonPackagingPlastic = data.nonPackagingPlastic;
    if (data.nonPlasticOrganic) body.nonPlasticOrganic = data.nonPlasticOrganic;
    if (uploadedImage) body.image = uploadedImage;
    updateQualityInput(params?.id as string, body);
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  /** Effects */
  useEffect(() => {
    getQualityInput(params.id as string);
    return () => {
      clearUploadedImage();
    };
  }, []);

  useEffect(() => {
    if (oneQualityInput?._id) {
      reset({
        dropDate: oneQualityInput.dropDate,
        totalWeight: oneQualityInput.totalWeight,
        pet: oneQualityInput.pet,
        pp: oneQualityInput.pp,
        hdpe: oneQualityInput.hdpe,
        ldpe: oneQualityInput.ldpe,
        ps: oneQualityInput.ps,
        drinkingCartoons: oneQualityInput.drinkingCartoons,
        misc: oneQualityInput.misc,
        nonPackagingPlastic: oneQualityInput.nonPackagingPlastic,
        nonPlasticOrganic: oneQualityInput.nonPlasticOrganic,
        qualityAssessment: oneQualityInput.qualityAssessment ?? undefined,
      });
      if (oneQualityInput.image) {
        setUploadedImage(oneQualityInput.image);
      }
    }
  }, [oneQualityInput]);

  useEffect(() => {
    if (updatedQualityInput) {
      clearUpdatedQualityInput();
      goBack();
    }
  }, [updatedQualityInput]);

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex w-1/2 justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-11">
          Quality Input
        </h1>
      </div>
      <div className=" w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md">
        <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
          <p className="font-satoshi font-medium text-base text-dark">
            Add quality input
          </p>
        </div>
        <div className="w-full px-7 py-7">
          <ControlledDatePicker
            name="dropDate"
            rules={{ required: true }}
            control={control}
            error={Boolean(errors?.dropDate)}
            label="Drop Date"
            placeholder="Add Drop Date here..."
          />
          <ControlledInput
            name="totalWeight"
            control={control}
            label="Total Weight (kg)"
            placeholder="Add total weight in kg"
            type="text"
            // rules={{ required: true }}
            rules={{ required: true, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.totalWeight ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("totalWeight") ?? 0)
            )}
          />
          <ControlledInput
            name="pet"
            control={control}
            label="PET"
            placeholder="Add PET"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.pet ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("pet") ?? 0)
            )}
          />
          <ControlledInput
            name="pp"
            control={control}
            label="PP"
            placeholder="Add PP"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.pp ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("pp") ?? 0)
            )}
          />
          <ControlledInput
            name="hdpe"
            control={control}
            label="HDPE"
            placeholder="Add HDPE"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.hdpe ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("hdpe") ?? 0)
            )}
          />
          <ControlledInput
            name="ldpe"
            control={control}
            label="LDPE"
            placeholder="Add LDPE"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.ldpe ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("ldpe") ?? 0)
            )}
          />
          <ControlledInput
            name="ps"
            control={control}
            label="PS"
            placeholder="Add PS"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.ps ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("ps") ?? 0)
            )}
          />
          <ControlledInput
            name="drinkingCartoons"
            control={control}
            label="Drinking Cartoons"
            placeholder="Add Drinking Cartoons"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.drinkingCartoons ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("drinkingCartoons") ?? 0)
            )}
          />
          <ControlledInput
            name="misc"
            control={control}
            label="Misc"
            placeholder="Add Misc"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.misc ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("misc") ?? 0)
            )}
          />
          <ControlledInput
            name="nonPackagingPlastic"
            control={control}
            label="Non Packaging Plastic"
            placeholder="Add Non Packaging Plastic"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.nonPackagingPlastic ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("nonPackagingPlastic") ?? 0)
            )}
          />
          <ControlledInput
            name="nonPlasticOrganic"
            control={control}
            label="Non Plastic Organic"
            placeholder="Add Non Plastic Organic"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.nonPlasticOrganic ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("nonPlasticOrganic") ?? 0)
            )}
          />
          <ControlledSelect
            name="qualityAssessment"
            control={control}
            rules={{ required: true }}
            error={Boolean(errors?.qualityAssessment)}
            label="Quality Assessment"
            options={[
              { _id: 1, title: "Bad" },
              { _id: 2, title: "Not so bad" },
              { _id: 3, title: "Fine" },
              { _id: 4, title: "Good" },
              { _id: 5, title: "Excellent" },
            ]}
            placeholder="Pick quality assessment here"
          />
          <FormUpload
            label="Image"
            onChange={onImageChange}
            imageUrl={uploadedImage as string}
            value={image}
          />
        </div>
      </div>
      <div className="w-1/2 flex flex-row items-center justify-end mt-[29px]">
        <SecondaryButton onClick={goBack} label="Cancel" />
        <PrimaryButton type="submit" className="ml-4" label="Save" />
      </div>
    </form>
  );
}

export default withProtectedRoute(EditQualityInputScreen);
